import React, { FC, KeyboardEvent } from 'react';

import styled from 'styled-components';

import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import UIButton, { EUIButtonVariant } from '@aquariuxfintech/aqx-trader-react-ui/button';
import UIFormItem, { EUIFormItemVariant } from '@aquariuxfintech/aqx-trader-react-ui/form';
import { ESpacing, ETypography } from '@aquariuxfintech/aqx-trader-react-ui/foundation';
import UIInput, { EUIInputVariant } from '@aquariuxfintech/aqx-trader-react-ui/input';
import UIText from '@aquariuxfintech/aqx-trader-react-ui/text';

import { AUTHENTICATION_ROUTES } from '@aqx-core/common/constants/router';
import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { useForgotPasswordForm } from '@aqx-core/common/hooks/form/use-forgot-password-form';
import { useForgotPassword } from '@aqx-core/common/services/forgot-password';
import { getEnterKeycode } from '@aqx-core/common/utils/keyboard';

import { IForgotPasswordScreen } from '..';
import AuthFormSecondaryAction from '../../shared/form-secondary-action';

interface IForgotPasswordFormProps extends IForgotPasswordScreen {}

const ForgotPasswordForm: FC<IForgotPasswordFormProps> = ({ isCRM = false }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { loading } = useForgotPassword();

  const { useFormMethods, submit, isDisabled, email, metatraderId } = useForgotPasswordForm(isCRM);
  const { formState, register } = useFormMethods;

  const {
    errors: { email: emailError, metatraderId: metatraderIdError }
  } = formState;

  const handleKeyDown = (event: KeyboardEvent) => {
    if (getEnterKeycode(event)) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <StyledContainer>
      <UIText typography={ETypography.DISPLAY_M_BOLD}>{t('Forgot password')}</UIText>
      <StyledDescription typography={ETypography.TEXT_XL_REGULAR}>
        {isCRM
          ? t('Enter your registered email address to reset your password.')
          : t('Enter your registered email address and account ID to reset your password.')}
      </StyledDescription>

      <FormProvider {...useFormMethods}>
        <StyledFormContainer onSubmit={submit}>
          <StyledFormInputsContainer>
            <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={'Email'} size="large">
              <UIInput
                {...register('email')}
                variant={EUIInputVariant.BORDERED_TEXT}
                placeholder={t('Enter your registered email')}
                value={email}
                error={emailError?.message}
                onKeyDown={handleKeyDown}
                dataTestId={EDataTestId.FORGOT_PASSWORD_EMAIL_INPUT}
                size="large"
              />
            </UIFormItem>
            {!isCRM && (
              <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={'Account ID'} size="large">
                <UIInput
                  {...register('metatraderId')}
                  variant={EUIInputVariant.BORDERED_TEXT}
                  placeholder={t('Enter your account ID')}
                  value={metatraderId}
                  error={metatraderIdError?.message}
                  onKeyDown={handleKeyDown}
                  dataTestId={EDataTestId.FORGOT_PASSWORD_ACCOUNT_ID_INPUT}
                  size="large"
                />
              </UIFormItem>
            )}
          </StyledFormInputsContainer>
          <StyledButtonContainer>
            <UIButton
              variant={EUIButtonVariant.FILLED_THEMED}
              stretch={true}
              loading={loading}
              disabled={isDisabled}
              dataTestId={EDataTestId.FORGOT_PASSWORD_NEXT_BUTTON}
              size="large"
            >
              {t('Next')}
            </UIButton>
          </StyledButtonContainer>
        </StyledFormContainer>
      </FormProvider>

      <AuthFormSecondaryAction
        actionText={t('Back to Log In')}
        onClickActionText={() => navigate(AUTHENTICATION_ROUTES.LOGIN)}
      />
    </StyledContainer>
  );
};

export default ForgotPasswordForm;

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 512px;
`;

const StyledDescription = styled(UIText)`
  margin-top: ${ESpacing.REGULAR}px;
  color: ${({ theme }) => theme.themeColors.text.subtle};
`;

const StyledFormContainer = styled.form`
  display: flex;
  flex-direction: column;
`;

const StyledFormInputsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${ESpacing.XL}px;
  margin-top: ${ESpacing.XXXL}px;
  margin-bottom: ${ESpacing.XL}px;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  margin-bottom: ${ESpacing.M}px;
`;
